import { colors } from '../theme'

// Proposal statuses + contract statuses both flow through this pill. The
// scheme map carries every label either flow can show so the component
// stays drop-in across both surfaces.
type ProposalStatus =
  | 'draft'
  | 'generated'
  | 'sent'
  | 'approved'
  | 'won'
  | 'lost'
type ContractStatus =
  | 'draft'
  | 'sent'
  | 'viewed'
  | 'signed'
  | 'declined'
  | 'expired'
  | 'void'
type Status = ProposalStatus | ContractStatus

// Brand-aligned status palette. Stack accents are functional only; in-flight
// workflow states use Master Purple, terminal-success uses the ONE green
// (#15803D, brand-token `success`) per parkify-brand-guidelines.
const SCHEME: Record<Status, { fg: string; bg: string; label: string }> = {
  // Shared
  draft:     { fg: colors.black,   bg: 'rgba(35,31,32,0.06)',  label: 'Draft' },
  sent:      { fg: colors.purple,  bg: 'rgba(127,63,152,0.10)', label: 'Sent' },
  // Proposal-only
  generated: { fg: colors.blue,    bg: 'rgba(33,64,154,0.10)', label: 'Generated' },
  approved:  { fg: '#15803D',      bg: 'rgba(21,128,61,0.10)',  label: 'Approved' },
  won:       { fg: colors.orange,  bg: 'rgba(241,101,33,0.12)', label: 'Won' },
  lost:      { fg: colors.red,     bg: 'rgba(237,28,36,0.10)',  label: 'Lost' },
  // Contract-only
  viewed:    { fg: colors.blue,    bg: 'rgba(33,64,154,0.10)',  label: 'Viewed' },
  signed:    { fg: '#15803D',      bg: 'rgba(21,128,61,0.10)',  label: 'Signed' },
  declined:  { fg: colors.red,     bg: 'rgba(237,28,36,0.10)',  label: 'Declined' },
  expired:   { fg: colors.gray,    bg: 'rgba(35,31,32,0.06)',   label: 'Expired' },
  void:      { fg: colors.gray,    bg: 'rgba(35,31,32,0.06)',   label: 'Void' },
}

export default function StatusPill({ status }: { status: Status }) {
  const s = SCHEME[status] ?? SCHEME.draft
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 12px',
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '1.2px',
        textTransform: 'uppercase',
        color: s.fg,
        background: s.bg,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: 'currentColor',
        }}
      />
      {s.label}
    </span>
  )
}
